import React, { useEffect, useState } from 'react'
import { usePublicSession } from '../contexts/PublicSessionContext'
import { useStudyRuntime } from '../contexts/StudyRuntimeContext'
import { PreloadLink } from './PreloadLink'
import { Transition } from '@headlessui/react'
import { Menu, X, LogOut, CreditCard } from 'lucide-react'
import { getStudyRoute } from '../lib/studyNavigation'
import './platform/platform.css'
import './public-folio.css'

const Navbar = ({ activePage = 'home' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, profile, signOut } = usePublicSession();
  const studyRuntime = useStudyRuntime();
  const [scrolled, setScrolled] = useState(false);
  const subscriptionType = String(profile?.subscription_type || '').toLowerCase();
  const hasPaidPlan = ['basic', 'pro', 'pro_ultra'].includes(subscriptionType);
  const canManageSubscription = Boolean(user && hasPaidPlan);
  const pendingStudyTabId = studyRuntime?.pendingTab || null;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSignOut = async () => {
    try {
      setIsMenuOpen(false);
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Navbar: Failed to sign out:', error);
      window.location.href = '/';
    }
  };

  const mainLinks = [
    { text: 'AI Tutor', href: getStudyRoute('ai-tutor'), activeMatch: 'ai-tutor', tabId: 'ai-tutor' },
    { text: 'Flashcards', href: getStudyRoute('flashcards'), activeMatch: 'flashcards', tabId: 'flashcards' },
    { text: 'Study Tracker', href: getStudyRoute('revision-tracker'), activeMatch: 'revision-tracker', tabId: 'revision-tracker' }
  ];

  return (
    <nav className={`ap-public-nav fixed top-0 w-full z-50 transition-all duration-300 font-sans ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="mx-auto flex max-w-[1520px] items-center justify-between px-4 py-2 sm:px-5 lg:px-6">
        <PreloadLink to="/" className="group flex items-center gap-3 relative z-50 overflow-visible py-1">
          <div className="ap-public-wordmark">
            <span aria-hidden="true"><svg viewBox="0 0 48 48"><path d="M24 2.5 27.2 20.8 45.5 24 27.2 27.2 24 45.5 20.8 27.2 2.5 24 20.8 20.8 24 2.5Z" fill="currentColor" /></svg></span>
            <strong>APDoneRight</strong>
          </div>
        </PreloadLink>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-8">
          <NavLink text="Home" href="/" isActive={activePage === 'home'} scrolled={scrolled} />
          {mainLinks.map((link) => (
            <NavLink
              key={link.href}
              text={link.text}
              href={link.href}
              isActive={activePage.includes(link.activeMatch)}
              isPending={pendingStudyTabId === link.tabId}
              studyTabId={link.tabId}
              scrolled={scrolled}
            />
          ))}
        </div>

        {/* Auth Buttons */}
        <div className="hidden md:flex items-center space-x-3">
          {user ? (
            <div className="flex items-center space-x-3">
              <div className="flex items-center gap-2 rounded-full border border-slate-200/80 bg-white px-3 py-1.5 shadow-sm">
                <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-xs font-bold">
                  {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                </div>
                <span className="text-sm font-medium text-slate-700 max-w-[150px] truncate">{user.email}</span>
              </div>
              {canManageSubscription && (
                <PreloadLink
                  to="/manage-subscription"
                  aria-label="Manage subscription"
                  title="Manage subscription"
                  className="p-2 rounded-full text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                >
                  <CreditCard size={18} />
                </PreloadLink>
              )}
              <button
                onClick={handleSignOut}
                title="Sign Out"
                className="p-2 rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <PreloadLink to="/signin" className="text-sm font-semibold text-slate-600 hover:text-teal-600 transition-colors">
                Log in
              </PreloadLink>
              <PreloadLink to="/signup" className="px-5 py-2.5 rounded-full bg-teal-500 text-white text-sm font-bold hover:bg-teal-600 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5">
                Get Started
              </PreloadLink>
            </div>
          )}
        </div>

        {/* Mobile Toggle */}
        <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="rounded-full p-2 text-slate-700 transition-colors hover:bg-slate-100 md:hidden">
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <Transition
        show={isMenuOpen}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 -translate-y-2"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-2"
      >
        <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-teal-100 shadow-lg p-4 flex flex-col space-y-4 max-h-[80vh] overflow-y-auto font-sans">
          <MobileLink to="/" onClick={() => setIsMenuOpen(false)}>Home</MobileLink>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-bold text-slate-400 uppercase mb-3 tracking-wider px-2">Study Tools</p>
            <div className="flex flex-col space-y-1">
              {mainLinks.map((link) => (
                <MobileLink
                  key={link.href}
                  to={link.href}
                  studyTabId={link.tabId}
                  isPending={pendingStudyTabId === link.tabId}
                  onClick={() => setIsMenuOpen(false)}
                  className="text-slate-600"
                >
                  {link.text}
                </MobileLink>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-5 pb-3">
            {user ? (
              <div className="flex flex-col gap-3 px-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold">
                    {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <span className="text-sm font-medium text-slate-700 truncate">{user.email}</span>
                </div>
                {canManageSubscription && (
                  <MobileLink to="/manage-subscription" onClick={() => setIsMenuOpen(false)} className="w-full text-left text-teal-600 font-medium hover:text-teal-700 transition-colors px-0">
                    Manage Subscription
                  </MobileLink>
                )}
                <button onClick={handleSignOut} className="w-full text-left text-rose-500 font-medium hover:text-rose-600 transition-colors py-2">Sign Out</button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <PreloadLink to="/signin" className="w-full py-2.5 text-center border border-slate-200 rounded-xl text-slate-600 font-semibold hover:border-teal-300 hover:text-teal-600 transition-colors">
                  Log in
                </PreloadLink>
                <PreloadLink to="/signup" className="w-full py-2.5 text-center bg-teal-500 text-white rounded-xl font-bold hover:bg-teal-600 transition-colors shadow-sm">
                  Get Started
                </PreloadLink>
              </div>
            )}
          </div>
        </div>
      </Transition>
    </nav>
  )
}

const NavLink = ({ text, href, isActive, isPending = false, scrolled, studyTabId = null }) => {
  const isHighlighted = isActive || isPending;
  return (
    <PreloadLink
      to={href}
      studyTabId={studyTabId}
      data-pending={isPending ? 'true' : 'false'}
      className={`text-sm font-semibold tracking-wide transition-colors duration-200 relative group py-1 ${isHighlighted ? 'text-teal-600' : 'text-slate-500 hover:text-teal-500'
        }`}
    >
      {text}
      <span className={`absolute bottom-0 left-0 h-0.5 bg-teal-500 transition-all duration-300 rounded-full ${isHighlighted ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
    </PreloadLink>
  );
};

const MobileLink = ({ to, children, className = '', isPending = false, onClick, studyTabId = null }) => {
  return (
    <PreloadLink
      to={to}
      onClick={onClick}
      studyTabId={studyTabId}
      data-pending={isPending ? 'true' : 'false'}
      className={`block py-2 font-medium px-4 rounded-lg transition-colors ${isPending ? 'text-teal-600 bg-teal-50/60' : 'text-slate-600 hover:text-teal-600 hover:bg-teal-50/50'} ${className}`}
    >
      {children}
    </PreloadLink>
  );
};

export default Navbar
